import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert
} from '@mui/material';
import PublicLicensingBoardCard from './PublicLicensingBoardCard';
import axios from 'axios';
import API_URL, { Token_Header_Key, Client_Username, Client_Password } from '../../config/config';
import '../../styles/Licensing-Boards-Landing.css';

interface ProcessedBoardData {
  id: string;
  name: string;
  generalCMEHours: string | number;
  topicsCount: number;
  licensurePeriod: string;
  website?: string;
  status: 'active' | 'archived';
  requirerType: string;
  requirements: any[];
}

interface Props {
  searchQuery: string;
}

const PublicLicensingBoardsLanding: React.FC<Props> = ({ searchQuery }) => {
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);
  const [boards, setBoards] = useState<ProcessedBoardData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadBoardsData();
  }, []);

  const loadBoardsData = async () => {
    setLoading(true);
    setError(null);
    try {
      const tokenResponse = await axios.post(`${API_URL}/generateToken`, {
        username: Client_Username,
        password: Client_Password,
        acepmemberstatus: 2
      });
      const token = (tokenResponse.data as any)?.cme_token;

      const response = await axios.get(`${API_URL}/management/boards-with-requirements`, {
        headers: { [Token_Header_Key]: token }
      });
      const boardsData: any[] = (response.data as any)?.certificationboards?.rows || [];

      const processedBoards: ProcessedBoardData[] = boardsData.map((board) => {
        const requirements = board.CME_Requirement || [];

        const generalCMEReq = requirements.find((r: any) => {
          const topicName = r.CME_Topic?.Name || r.Topic?.Name || r.TopicName || '';
          return topicName && (topicName.toLowerCase().includes('general') || topicName.toLowerCase() === 'none');
        });

        const rawGeneralHours = generalCMEReq
          ? (generalCMEReq.CMEHours ?? generalCMEReq.CME_Hours ?? generalCMEReq.Hours ?? 0)
          : null;

        const generalHours = rawGeneralHours === null || rawGeneralHours === 0 ? '—' : `${rawGeneralHours} Hrs`;
        const period = (generalCMEReq && rawGeneralHours !== 0 && rawGeneralHours !== null && generalCMEReq.Frequency)
          ? `${generalCMEReq.Frequency}`
          : '—';

        const processedRequirements = requirements.map((req: any) => {
          const topicName = req.CME_Topic?.Name || req.Topic?.Name || req.TopicName || '';
          const isCreditType = req.CME_Topic?.isCreditType || req.CME_Topic?.IsCreditType ||
            req.Topic?.isCreditType || req.Topic?.IsCreditType || false;
          return {
            id: req.Id,
            topic: topicName && topicName.toLowerCase() !== 'none' ? topicName : 'General CME',
            hours: req.CMEHours || req.CME_Hours || req.Hours || 0,
            description: req.Description,
            frequency: req.Frequency,
            interval: req.Interval,
            statute: req.Statute,
            notes: req.Notes,
            isCreditType,
            lastUpdated: req.UpdatedAt ? new Date(req.UpdatedAt).toLocaleDateString() : undefined
          };
        }).sort((a: any, b: any) => {
          const aIsGeneral = a.topic.toLowerCase().includes('general');
          const bIsGeneral = b.topic.toLowerCase().includes('general');
          if (aIsGeneral && !bIsGeneral) return -1;
          if (!aIsGeneral && bIsGeneral) return 1;
          return 0;
        });

        let isActive = true;
        if (board.IsActive !== undefined) isActive = board.IsActive === true;
        else if (board.Active !== undefined) isActive = board.Active === true;
        else if (board.Status !== undefined) isActive = board.Status === 'Active' || board.Status === 1;
        else if (board.IsDeleted !== undefined) isActive = !board.IsDeleted;

        const status: 'active' | 'archived' = isActive ? 'active' : 'archived';
        const categoryName = board.CME_ProfileCategory?.Name || '';

        return {
          id: board.Id,
          name: board.Name,
          generalCMEHours: generalHours,
          topicsCount: processedRequirements.filter((r: any) => !r.isCreditType && r.topic.toLowerCase() !== 'general cme').length,
          licensurePeriod: period,
          website: board.Website,
          status,
          requirerType: categoryName,
          requirements: processedRequirements
        };
      });

      setBoards(processedBoards);
    } catch (err: any) {
      console.error('PublicLicensingBoardsLanding: failed to load boards:', err?.response?.data || err?.message || err);
      setError('Failed to load licensing boards. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCardExpand = (id: string) => {
    setExpandedCardId(expandedCardId === id ? null : id);
  };

  const filteredBoards = boards
    .filter(b => b.requirerType === 'State Medical Board')
    .filter(b => !searchQuery.trim() || b.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Box className="licensing-boards-landing-container" sx={{ marginTop: '0px' }}>
      <Box sx={{ padding: '24px', backgroundColor: '#FFFFFF' }}>

        {loading && <div className="spinner"></div>}

        {error && <Alert severity="error" sx={{ marginBottom: '16px' }}>{error}</Alert>}

        {!loading && !error && (
          filteredBoards.length === 0 ? (
            <Alert severity="info">
              No licensing boards found.{searchQuery ? ' Try adjusting your search.' : ''}
            </Alert>
          ) : (
            <>
              {/* Table Header */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '3fr 0.8fr 0.8fr 1fr 1.6fr', md: '3fr 0.8fr 0.8fr 1fr 1.6fr 60px' },
                gap: '16px',
                padding: '0px 20px 0px 0px',
                backgroundColor: '#FFFFFF',
                marginBottom: '8px'
              }}>
                <Typography sx={{ fontWeight: 600, fontSize: '13px', color: 'black', textAlign: 'left' }}>Licensing Board Name</Typography>
                <Typography sx={{ fontWeight: 600, fontSize: '13px', color: 'black' }}>General CME</Typography>
                <Typography sx={{ fontWeight: 600, fontSize: '13px', color: 'black' }}># of Topics</Typography>
                <Typography sx={{ fontWeight: 600, fontSize: '13px', color: 'black' }}>Licensure Period</Typography>
                <Typography sx={{ fontWeight: 600, fontSize: '13px', color: 'black', textAlign: 'left' }}>Website</Typography>
                <Box />
              </Box>

              <Box className="licensing-boards-grid">
                {filteredBoards.map((board) => (
                  <PublicLicensingBoardCard
                    key={board.id}
                    id={board.id}
                    name={board.name}
                    generalCMEHours={board.generalCMEHours}
                    topicsCount={board.topicsCount}
                    licensurePeriod={board.licensurePeriod}
                    website={board.website}
                    status={board.status}
                    requirements={board.requirements}
                    isExpanded={expandedCardId === board.id}
                    onExpand={handleCardExpand}
                  />
                ))}
              </Box>
            </>
          )
        )}
      </Box>
    </Box>
  );
};

export default PublicLicensingBoardsLanding;
